import axiosInstance from '../utils/axiosInstance';

const get = (url, params) => axiosInstance.get(url, { params }).then((r) => r.data);
const post = (url, body) => axiosInstance.post(url, body || {}).then((r) => r.data);
const patch = (url, body) => axiosInstance.patch(url, body).then((r) => r.data);
const del = (url) => axiosInstance.delete(url).then((r) => r.data);

// DRF list endpoints are paginated ({count, results}); unwrap to the array.
const arr = (data) => (data && Array.isArray(data.results) ? data.results : data);
// JSON body, or multipart (files appended under `files`) when there are uploads.
// Nulls become '' so the server reads "cleared", not the string "null".
const toBody = (data, files = []) => {
  if (!files.length) return data;
  const fd = new FormData();
  Object.entries(data).forEach(([k, v]) => { if (v !== undefined) fd.append(k, v ?? ''); });
  files.forEach((f) => fd.append('files', f));
  return fd;
};

export const api = {
  suppliers: {
    list: (params) => get('api/suppliers/', params),
    get: (id) => get(`api/suppliers/${id}/`),
    setStatus: (id, account_status) => post(`api/suppliers/${id}/set_status/`, { account_status }),
    remind: (id, date) => post(`api/suppliers/${id}/remind/`, { date }),
    setSourced: (id, force_sourced) => post(`api/suppliers/${id}/set_sourced/`, { force_sourced }),
    top: (limit = 5, type, unit) => get('api/suppliers/top/', { limit, ...(type ? { type } : {}), ...(unit ? { unit } : {}) }),
    setUnit: (id, business_unit_override) => post(`api/suppliers/${id}/set_unit/`, { business_unit_override }),
    paymentStats: (id) => get(`api/suppliers/${id}/payment_stats/`),
    follow: (id, prefs) => post(`api/suppliers/${id}/follow/`, prefs || {}),
    unfollow: (id) => post(`api/suppliers/${id}/unfollow/`),
    comments: (id) => get(`api/suppliers/${id}/comments/`),
    addComment: (id, body, mentions, gorgias_url = '', files = []) => {
      if (files && files.length) {
        // Multipart when there are attachments; mentions ride along as JSON.
        const fd = new FormData();
        fd.append('body', body || '');
        fd.append('gorgias_url', gorgias_url || '');
        fd.append('mentions', JSON.stringify(mentions || []));
        files.forEach((f) => fd.append('attachments', f));
        return post(`api/suppliers/${id}/comments/`, fd);
      }
      return post(`api/suppliers/${id}/comments/`, { body, mentions, gorgias_url });
    },
    editComment: (commentId, { body, files = [], remove = [] } = {}) => {
      if ((files && files.length) || (remove && remove.length)) {
        const fd = new FormData();
        if (body !== undefined) fd.append('body', body || '');
        if (remove.length) fd.append('remove_attachments', JSON.stringify(remove));
        files.forEach((f) => fd.append('attachments', f));
        return patch(`api/suppliers/comments/${commentId}/`, fd);
      }
      return patch(`api/suppliers/comments/${commentId}/`, { body });
    },
    deleteComment: (commentId) => del(`api/suppliers/comments/${commentId}/`),
    attachmentBlob: (attId) =>
      axiosInstance.get(`api/suppliers/comments/attachments/${attId}/`, { responseType: 'blob' }).then((r) => r.data),
    history: (id) => get(`api/suppliers/${id}/history/`),
    odooVendors: (q) => get('api/suppliers/odoo_vendors/', { q }),
    addVendor: (partner_id, name) => post('api/suppliers/add_vendor/', { partner_id, name }),
  },
  invoices: {
    // Returns the raw paginated payload ({count, results}).
    list: (params) => get('api/invoices/', params),
    get: (id) => get(`api/invoices/${id}/`),
    update: (id, body) => patch(`api/invoices/${id}/`, body),
    flag: (id) => post(`api/invoices/${id}/flag/`),
    unflag: (id) => post(`api/invoices/${id}/unflag/`),
    comments: (id) => get(`api/invoices/${id}/comments/`).then(arr),
    addComment: (id, body, mentions) =>
      post(`api/invoices/${id}/comments/`, { body, mentions }),
    chatter: (id) => get(`api/invoices/${id}/chatter/`),
    payments: (id) => get(`api/invoices/${id}/payments/`),
    proofPdf: (id) => axiosInstance.get(`api/invoices/${id}/proof/`, { responseType: 'blob' }).then((r) => r.data),
    // Combined proof-of-payment PDF for many bills; returns the blob + how many
    // were included / skipped (paid but no reconciled payment in Odoo).
    proofs: (ids) => axiosInstance.post('api/invoices/proofs/', { ids }, { responseType: 'blob' })
      .then((r) => ({ blob: r.data, included: Number(r.headers['x-proofs-included'] || 0), skipped: Number(r.headers['x-proofs-skipped'] || 0) })),
    pdfBlob: (id) => axiosInstance.get(`api/invoices/${id}/pdf/`, { responseType: 'blob' }).then((r) => r.data),
    proofBlob: (id, attId) => axiosInstance.get(`api/invoices/${id}/pdf/`, { params: { attachment_id: attId }, responseType: 'blob' }).then((r) => r.data),
    stats: (params) => get('api/invoices/stats/', params),
  },
  notifications: {
    list: () => get('api/notifications/').then(arr),
    unreadCount: () => get('api/notifications/unread_count/'),
    markRead: (id) => post(`api/notifications/${id}/read/`),
    markAllRead: () => post('api/notifications/read_all/'),
  },
  refunds: {
    list: (view = 'active') => get('api/refunds/', { view }).then(arr),
    create: (data) => {
      const { bank_image, ...rest } = data;
      if (bank_image) {
        const fd = new FormData();
        Object.entries(rest).forEach(([k, v]) => fd.append(k, v ?? ''));
        fd.append('bank_image', bank_image);
        return post('api/refunds/', fd);
      }
      return post('api/refunds/', rest);
    },
    update: (id, body) => {
      const { bank_image, ...rest } = body || {};
      if (bank_image) {
        const fd = new FormData();
        Object.entries(rest).forEach(([k, v]) => { if (v !== undefined) fd.append(k, v ?? ''); });
        fd.append('bank_image', bank_image);
        return patch(`api/refunds/${id}/`, fd);
      }
      return patch(`api/refunds/${id}/`, rest);
    },
    remove: (id) => del(`api/refunds/${id}/`),
    // look the pending refunds' credit notes up in Odoo; returns the refreshed active rows
    checkOdoo: () => post('api/refunds/check_odoo/'),
    // pay via Airwallex (payers only) - same sheet as the pro-formas
    paymentInfo: (id) => get(`api/refunds/${id}/payment_info/`),
    pay: (id, { account_id = null, reference = '', address = null, entity_type = '', name = '', bank = null } = {}) =>
      post(`api/refunds/${id}/pay/`, { account_id, reference, address, entity_type, name, bank }),
    checkTransfer: (id) => post(`api/refunds/${id}/check_transfer/`),
    confirmationLetter: (id) => post(`api/refunds/${id}/confirmation_letter/`),
    proofBlob: (id) => axiosInstance.get(`api/refunds/${id}/proof/`, { responseType: 'blob' }).then((r) => r.data),
    // link a refund to its Odoo customer (no credit note ref to find it from)
    odooCustomers: (q) => get('api/refunds/odoo_customers/', { q }),
    linkCustomer: (id, partner_id) => post(`api/refunds/${id}/link_customer/`, { partner_id }),
    // tell the customer the refund was sent: GET = prefilled draft, POST = send (+ finalize)
    notifyCustomerDraft: (id) => get(`api/refunds/${id}/notify_customer/`),
    notifyCustomer: (id, { to, subject, body, finalize = true, via = 'email' }) =>
      post(`api/refunds/${id}/notify_customer/`, { to, subject, body, finalize, via }),
    setStatus: (id, status, note = '') => post(`api/refunds/${id}/set_status/`, { status, note }),
    bankImageBlob: (id) =>
      axiosInstance.get(`api/refunds/${id}/bank_image/`, { responseType: 'blob' }).then((r) => r.data),
  },
  proformas: {
    // view: active | archived | all. Lists are small; ask for plenty so nothing is cut.
    list: (view = 'active', params = {}) => get('api/proformas/', { view, limit: 500, ...params }).then(arr),
    get: (id) => get(`api/proformas/${id}/`),
    // Multipart whenever files ride along (the proforma PDF); plain JSON otherwise.
    create: (data, files = []) => post('api/proformas/', toBody(data, files)),
    update: (id, data, { files = [], remove = [] } = {}) => {
      const body = toBody({ ...data, ...(remove.length ? { remove_files: JSON.stringify(remove) } : {}) }, files);
      return patch(`api/proformas/${id}/`, body);
    },
    remove: (id) => del(`api/proformas/${id}/`),
    setStatus: (id, status, note = '') => post(`api/proformas/${id}/set_status/`, { status, note }),
    history: (id) => get(`api/proformas/${id}/history/`),
    // payment sheet (payers only): beneficiary from Odoo + amount + communication
    paymentInfo: (id) => get(`api/proformas/${id}/payment_info/`),
    // via: 'airwallex' creates the transfer (account_id = the Odoo bank account
    // picked on the sheet); 'manual' records a transfer made from the bank.
    pay: (id, { via = 'manual', account = '', account_id = null, note = '', reference = '', address = null, bank = null } = {}) =>
      post(`api/proformas/${id}/pay/`, { via, account, account_id, note, reference, address, bank }),
    checkTransfer: (id) => post(`api/proformas/${id}/check_transfer/`),
    // (re)fetch Airwallex's transfer confirmation PDF onto the pro-forma
    confirmationLetter: (id) => post(`api/proformas/${id}/confirmation_letter/`),
    // tell the vendor: GET = prefilled draft, POST = send (+ finalize)
    notifyVendorDraft: (id) => get(`api/proformas/${id}/notify_vendor/`),
    notifyVendor: (id, { to, subject, body, finalize = true, via = 'email' }) =>
      post(`api/proformas/${id}/notify_vendor/`, { to, subject, body, finalize, via }),
    // connection check (payers only): fresh login, EUR balance, webhook config
    airwallexStatus: () => get('api/proformas/airwallex_status/'),
    comments: (id) => get(`api/proformas/${id}/comments/`),
    addComment: (id, body, mentions = [], files = []) => {
      if (files.length) {
        const fd = new FormData();
        fd.append('body', body || '');
        fd.append('mentions', JSON.stringify(mentions || []));
        files.forEach((f) => fd.append('attachments', f));
        return post(`api/proformas/${id}/comments/`, fd);
      }
      return post(`api/proformas/${id}/comments/`, { body, mentions });
    },
    editComment: (commentId, { body, files = [], remove = [] } = {}) => {
      if (files.length || remove.length) {
        const fd = new FormData();
        if (body !== undefined) fd.append('body', body || '');
        if (remove.length) fd.append('remove_attachments', JSON.stringify(remove));
        files.forEach((f) => fd.append('attachments', f));
        return patch(`api/proformas/comments/${commentId}/`, fd);
      }
      return patch(`api/proformas/comments/${commentId}/`, { body });
    },
    deleteComment: (commentId) => del(`api/proformas/comments/${commentId}/`),
    fileBlob: (fileId) =>
      axiosInstance.get(`api/proformas/files/${fileId}/`, { responseType: 'blob' }).then((r) => r.data),
    deleteFile: (fileId) => del(`api/proformas/files/${fileId}/`),
  },
  todo: {
    // open-item counts for the sidebar badges: { proformas, refunds }
    counts: () => get('api/todo/counts/'),
  },
  expenseNotes: {
    // approved notes whose PDF waits in Odoo Documents, with no vendor bill yet
    waiting: () => get('api/expense-notes/waiting/').then((d) => d.results || []),
  },
  sync: {
    status: () => get('api/sync/status/'),
    run: (auto = false) => post('api/sync/run/', auto ? { auto: true } : {}),
  },
  users: {
    list: () => get('api/users/list/'),
    me: () => get('api/users/me/'),
  },
  encoding: {
    suppliers: (params) => get('api/encoding/suppliers/', params),
    createSupplier: (body) => post('api/encoding/suppliers/', body),
    updateSupplier: (partnerId, body) => patch(`api/encoding/suppliers/${partnerId}/`, body),
    deleteSupplier: (partnerId) => del(`api/encoding/suppliers/${partnerId}/`),
    rules: () => get('api/encoding/rules/'),
    createRule: (body) => post('api/encoding/rules/', body),
    updateRule: (code, body) => patch(`api/encoding/rules/${code}/`, body),
    deleteRule: (code) => del(`api/encoding/rules/${code}/`),
    references: () => get('api/encoding/references/'),
    changelog: (params) => get('api/encoding/changelog/', params),
  },
  paypal: {
    // Balances in every currency held, each converted into EUR at the ECB
    // reference rate, and what each can pay out while keeping `reserve` EUR in
    // every currency. Payers only. `check` forces a fresh login.
    balances: (reserve = 5000, check = false) =>
      get('api/paypal/balances/', { target: 'EUR', reserve, ...(check ? { check: 1 } : {}) }),
  },
  game: {
    leaderboard: () => get('api/petanque/leaderboard/'),
    // run = { score, round, char, ball, courses: [{ id, name, dist, pts, tag }] }
    submitScore: (run) => post('api/petanque/leaderboard/', run),
  },
};

export default api;
